// The shared table. One row/column model for every list surface, tanstack v8
// underneath, the house dialects layered on top. Behaviors live HERE as toggles;
// domain content (pills, chips, editors, links) arrives from the page as cell
// renderers and slots — this component never knows what a cart or a pill is.
//
// The component owns the WHOLE vessel: optional strip, the scrolling field,
// the header, an optional column-totals row inside the table and an optional
// prose footer below it. The face is the kit list (Spark's project list): each
// record a slat one tone above the surface the table sits on (`on`), split by
// 1px of that surface, with the header, totals and footer on the surface
// itself. No border lines anywhere. Pages pass content for the strip and
// footer and style nothing about the container. tableFace.ts holds the values.
//
// The three contracts consumers must honor:
//  1. `columns` is referentially stable — module-scope, or useMemo'd on
//     genuinely stable deps — and cell renderers are never fresh per render.
//     flexRender treats any function as a component TYPE, so a fresh identity
//     remounts the whole cell subtree on every render — which kills animation
//     refs and closes open editors. State that changes per keystroke travels
//     by the `meta` prop instead of by a rebuilt columns array.
//  2. Every column carries an explicit `id`. Sortable columns declare an
//     `accessorFn` returning the sort-typed value, with `undefined` (never
//     null) for empties: a display-only column silently cannot sort, and null
//     makes the comparator inconsistent.
//  3. The keyboard dialect follows the table's VISUAL row order (the tanstack
//     row model), never the consumer's array. Row order is held once.
//
// Markup is a real <table>/<tr>/<td> on purpose: the sheet cell dialect
// (SmartCharger donor, migrates last) hit-tests via td.cellIndex and
// tr.sectionRowIndex, and existing tests query columnheader roles.
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CsvValue, csvFilename, downloadCsv, toCsv } from '../../utils/csv';
import {
  ColumnDef,
  Row,
  SortingFn,
  SortingState,
  TableMeta,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useViewportFill } from '../ui/viewport';
import { FOCUS } from '../ui/motion';
import { IconChevron, IconChevronDown } from '../ui/icons';
import SubRow from './SubRow';
import { BAND_KEY, SUB_KEY, useRowMotion } from './rowMotion';
import {
  BAND_CELL,
  BAND_MOTION,
  BAND_ROW,
  BODY_BUSY,
  BODY_REST,
  BUSY_LINE,
  BUSY_SWEEP,
  BUSY_TRACK,
  CELL,
  Corner,
  FOOTER,
  FOOTER_CELL,
  HEADER_CELL,
  LANDING_GLOW_MS,
  PINNED_CELL,
  ROW_BASE,
  ROW_ENTER,
  ROW_GLOW,
  ROW_LAND,
  ROW_LEAVE,
  SCROLL_THUMB,
  SELECTED_ROW,
  STRIP,
  TABLE_TEXT,
  TONES,
  TableDensity,
  TableOn,
  cornerStyle,
  mergeStyle,
} from './tableFace';

export type { TableDensity, TableOn } from './tableFace';

declare module '@tanstack/react-table' {
  interface SortingFns {
    house: SortingFn<unknown>;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    /** Cell + header text alignment; default left. */
    align?: 'right' | 'center';
    /** Adds tabular-nums to the cell (money, quantities, anything compared). */
    numeric?: boolean;
    /** Appended to the header cell's class list. */
    headerClass?: string;
    /** Appended to the body cell's class list. */
    cellClass?: string;
    /**
     * The cell's value for Export CSV. Wins over the sort accessor, which
     * often returns a sort-shaped value (lowercased, cents) rather than the
     * one a spreadsheet should hold. A column with neither this nor an
     * accessor is display-only (a chip, a bar, a button) and is left out of
     * the file. Money as dollars, dates as YYYY-MM-DD, statuses as their
     * on-screen labels.
     */
    csv?: (row: TData) => CsvValue;
    /** Column name in the file when `header` is not a plain string. */
    csvHeader?: string;
    /** Appended to the column-footer cell's class list (the tfoot band). */
    footerClass?: string;
    /**
     * td-level attributes per row (e.g. the truncation `title` tooltip —
     * it belongs on the td, and jsdom folds `title` into accessible names).
     */
    cellAttrs?: (row: TData) => React.TdHTMLAttributes<HTMLTableCellElement>;
  }
}

// Today's collation, verbatim: numbers numerically, strings with numeric
// collation so FF-AP-02-9 sorts before FF-AP-02-10. Registered instead of
// tanstack's 'auto', which picks its comparator by sampling filtered rows
// past index ten — an empty sample under eleven rows, so tests and prod
// would sort differently.
const houseCompare: SortingFn<unknown> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b ? 0 : a < b ? -1 : 1;
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export interface DataTableHandle {
  /** Selection back to the first row (the lane-chip / view-switch reset). */
  resetSelection: () => void;
  /**
   * What Export CSV would write: the visible columns that carry a value
   * (`meta.csv`, else the sort accessor), over the rows in the table's
   * current sort order. Grouping is a display fold and does not reorder the
   * file. Exposed for tests and for a page that wants to hand the rows to
   * something other than a download.
   */
  exportRows: () => { headers: string[]; rows: CsvValue[][] };
  /**
   * Downloads `<basename>-<date>.csv` of `exportRows()` and returns the row
   * count. Returns `null` and writes nothing while the table is `loading`:
   * the row model still holds the PREVIOUS answer under the spinner, and a
   * file of last filter's rows named for this one is the silent kind of wrong.
   * Pages disable the item while loading too; this is the floor under them.
   */
  exportCsv: (basename: string) => number | null;
}

export interface RowKeyboardOpts<T> {
  /** Enter on the selected row. */
  onOpen: (row: T) => void;
  /** D on the selected row; omit it and the key stays inert. */
  onDiscard?: (row: T) => void;
}

export interface GroupByOpts<T> {
  key: (row: T) => string;
  /**
   * Content only — the component supplies the <tr><td colSpan>. Return null
   * (or nothing) and the group gets NO header row at all while its rows still
   * render: a band over the one thing everybody came to read is furniture.
   * The third argument carries the group's own collapse state, for copy that
   * has to say which way it is facing.
   */
  renderHeader: (key: string, rows: T[], ctx?: { collapsed: boolean }) => React.ReactNode;
  /**
   * Group ordering. Defaults to localeCompare on the keys — groups stay
   * alphabetical regardless of the active column sort, while rows inside a
   * group keep the sort order. First-appearance order is deliberately not
   * the default.
   */
  order?: (keys: string[]) => string[];
  /**
   * Which groups may collapse. A collapsible group's header row becomes the
   * toggle, and a collapsed group's rows leave the field AND the keyboard
   * order — they are not there to be walked past.
   */
  collapsible?: (key: string) => boolean;
  /**
   * Which collapsible groups START collapsed. Omitted means expanded: opting
   * into collapsibility must not hide data by default. This is read every
   * render for any key the user has not touched, so a refetch that changes
   * the answer re-derives cleanly.
   */
  initialCollapsed?: (key: string) => boolean;
  rowClass?: string;
  cellClass?: string;
}

/**
 * One group's whole plan, derived ONCE (see the memo in the component) so the
 * renderer and the keyboard dialect cannot disagree about which rows exist.
 */
interface GroupPlan<T> {
  key: string;
  rows: Row<T>[];
  /** The consumer's header content, already rendered. */
  header: React.ReactNode;
  /** renderHeader returned nothing: no header row for this group. */
  headerless: boolean;
  /** Resolved, so a headerless group can never be collapsible. */
  collapsible: boolean;
  collapsed: boolean;
}

/** One body line in drawn order. `index` is a row's place in the keyboard sequence. */
type Line<T> =
  | { kind: 'band'; key: string; group: GroupPlan<T> }
  | { kind: 'row'; key: string; row: Row<T>; index: number };

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  /** Row identity for React keys and animation refs (composite keys welcome). */
  getRowId?: (row: T, index: number) => string;
  /**
   * The escape valve for contract 1. Columns must stay referentially stable,
   * so anything that changes per keystroke (line drafts, their onChange, a
   * busy flag) cannot live in a closure inside a cell renderer — a fresh
   * `columns` array remounts every cell and throws the caret out of an
   * in-cell input. Put that state HERE instead: it reaches a cell as
   * `info.table.options.meta`, and changing it re-renders the cells without
   * remounting them.
   *
   * Typed `object` deliberately. `Record<string, unknown>` would reject an
   * `interface`-typed bag (interfaces carry no implicit index signature), and
   * tanstack's own `TableMeta` is a globally augmentable interface, so typing
   * the prop as that would let one consumer's augmentation dictate the shape
   * for all of them. The component never looks inside; the consumer casts on
   * the read side: `info.table.options.meta as PoLineMeta`.
   */
  meta?: object;

  // --- behavior toggles, all off by default ---
  sortable?: boolean;
  initialSorting?: SortingState;
  /**
   * Observe the current sort. Fires AFTER commit, including once on mount with
   * the initial sort, so a consumer persisting it (localStorage, per-tab
   * memory) writes the value it already has — idempotent, never a loop. The
   * component still owns the state; this is readback, not control, because a
   * fully controlled sort would let a consumer put the table in a state the
   * locked options forbid (a cleared sort, a multi-sort).
   */
  onSortingChange?: (sorting: SortingState) => void;
  /** Capability-driven COLUMN visibility (hidden = no th, no td at all). */
  columnVisibility?: VisibilityState;
  /** Presence enables J/K/Enter(/D) against the visual row order. */
  rowKeyboard?: RowKeyboardOpts<T>;
  groupBy?: GroupByOpts<T>;

  // --- the face ---
  /**
   * The surface the table sits on: the page (`ground`, default) or a panel
   * (`panel`). Rows step one tone up from it and the header band one more;
   * the totals, footer, row gaps and corner cuts are painted in it, so a wrong
   * answer shows as invisible rows and dark corners.
   */
  on?: TableOn;
  /** `compact` for dense tooling and tables nested in a panel or a disclosure. */
  density?: TableDensity;
  /** Alternate row tones instead of gaps, for wide dense rows read across. */
  striped?: boolean;
  /** The first visible column holds still while the field scrolls sideways. */
  pinFirstColumn?: boolean;
  /** Column names for screen readers only (a message log, a pasted table with no head row). */
  hideHeader?: boolean;
  /**
   * A fixed cap on the field, any CSS length ('16rem', '500px'). The rows
   * scroll inside it under a held header. `stickyHeader` is the other cap: the
   * rest of the viewport, measured.
   */
  maxHeight?: string;

  // --- vessel slots; content only, the component supplies the place ---
  /** Working strip above the header (counts, legends, live regions). */
  strip?: React.ReactNode;
  /**
   * Footer strip below the field: roll-up PROSE (counts, a grand total in a
   * sentence). Per-column totals are a different thing — give the columnDef a
   * `footer` and they land in a tfoot inside the table, aligned under the
   * column each one totals.
   */
  footer?: React.ReactNode;
  /** The header holds at the top of the field while the rows scroll; the vessel caps itself at the rest of the viewport. */
  stickyHeader?: boolean;

  // --- states ---
  /**
   * With rows on screen this is a refresh: the rows stay, dimmed under a working line, and the
   * table is aria-busy. `loadingContent` shows only while there are no rows yet, so a page that
   * empties its data when it starts loading gets the loading look every time.
   */
  loading?: boolean;
  loadingContent?: React.ReactNode;
  empty: React.ReactNode;
  /**
   * 'row' (default): loading (before any rows) and empty render as a full-width
   * row under the header. 'replace': they render in the shell's field INSTEAD of the table
   * — no thead, no table markup, but the vessel (shell, strip, footer)
   * persists so the page does not flicker between a box and a table. The AP
   * queue is on 'replace'.
   */
  emptyMode?: 'row' | 'replace';

  // --- class hooks; the component owns the shell and the scroll container ---
  /** Width and type size of the table; the gap structure is added regardless. */
  tableClass?: string;
  headerRowClass?: string;
  /** Table-wide header cell default; per-column meta.headerClass appends. */
  headerCellClass?: string;
  /** Table-wide body cell default; per-column meta.cellClass appends. */
  cellClass?: string;
  /** Table-wide tfoot cell default; per-column meta.footerClass appends. */
  footerCellClass?: string;
  /**
   * Per-row attributes. A row on its way out (it fades for 150ms after the data drops it) is
   * asked too, with `index` -1, and its click handler is dropped.
   */
  rowProps?: (
    row: T,
    ctx: { index: number; selected: boolean }
  ) => React.HTMLAttributes<HTMLTableRowElement> & Record<string, unknown>;
  /**
   * Content for a full-width row rendered directly under a data row, or
   * nothing. The pair travels together under sort. The sub-row is not a
   * member of the row sequence: the keyboard dialect and the landing latch
   * never see it. The page owns which rows are open (state it reads through
   * `meta`) and the sub-row's own padding. It grows in when it opens and
   * dissolves out when it closes, so it stays in the DOM (aria-hidden) for the
   * exit's 150ms after the function stops returning it.
   */
  renderSubRow?: (row: T) => React.ReactNode;
}

const DEFAULT_HEADER_ROW = '';
// Header cells never wrap (tableFace HEADER_CELL carries whitespace-nowrap): a
// two-word label folding under width pressure reads as breakage on every page
// at once; the field's own horizontal scroll is the vessel's answer to a tight
// viewport.
//
// Sticky lives on the TH, not the TR: a sticky tr does not hold its children
// in place across browsers, and the th's band is opaque anyway so rows never
// show through as they pass under it.
//
// `--dt-sticky-top` is the escape hatch for app chrome overlapping the field.
// INVESTIGATED 2026-08-21: nothing needs it today. The app's NavBar is a
// plain in-flow `<header>` (NavBar/NavBar.tsx), `.App` sets only min-height
// and a background (App.css), and `<main>` carries no offset — no fixed or
// sticky chrome sits above a table anywhere, so the 0px default is right.
// Set the variable in index.css if the navbar ever becomes sticky.
//
// WHAT STICKY HOLDS AGAINST: the field below, not the page. `overflow-x-auto`
// makes overflow-y compute to auto too, so that div is the nearest scrollport
// and the band tracks ITS scroll offset. That is why `stickyHeader` ALSO caps
// the shell's height: without a cap the field never scrolls vertically and
// the band would travel with the page, classes present but inert. The cap is
// MEASURED, not fixed (see the effect in the component): a fixed 70svh ignored
// everything above the table, so every page overflowed by its own chrome.
const STICKY_HEADER_CELL = 'sticky top-[var(--dt-sticky-top,0px)] z-[2]';
const PINNED_HEADER_CELL = 'sticky left-0 z-[1]';
// The pinned corner sits above both the sticky header and the pinned cells.
const PINNED_STICKY_HEADER_CELL = 'sticky top-[var(--dt-sticky-top,0px)] left-0 z-[3]';
// A row on its way out leaves the tab order and the accessibility tree at once. React 17 does not
// type `inert`; it passes the lowercase attribute through.
const LEAVING = { 'aria-hidden': true, inert: '' } as object;
const LEAVING_STOP = { 'aria-hidden': true, inert: '', tabIndex: -1 } as object;

// The landing charm is gated below so a view switch, the first paint, or a
// widened filter never animates a whole table — that is reflow noise, not
// charm. A landing party larger than this is a set change, not a landing.
const LANDING_LIMIT = 3;
const NO_IDS: ReadonlySet<string> = new Set();

// Stable empty object: a fresh {} per render would invalidate tanstack's
// per-row visible-cell memo on every render for consumers that pass no
// columnVisibility at all.
const NO_VISIBILITY: VisibilityState = {};

// Alignment comes from meta.align ONLY — never from the class strings, where
// a left and a right utility on one element resolve by stylesheet order, not
// by which one the consumer wrote last. Explicit left because browsers center
// th content by default.
function alignClass(align?: 'right' | 'center'): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function DataTableInner<T>(
  {
    data,
    columns,
    getRowId,
    // Aliased: `meta` is taken three times inside this component for a
    // COLUMN's meta, and one name for two things is how the wrong one gets
    // read.
    meta: tableMeta,
    sortable = false,
    initialSorting,
    onSortingChange,
    columnVisibility,
    rowKeyboard,
    groupBy,
    on = 'ground',
    density = 'comfortable',
    striped = false,
    pinFirstColumn = false,
    hideHeader = false,
    maxHeight,
    strip,
    footer,
    stickyHeader = false,
    loading = false,
    loadingContent,
    empty,
    emptyMode = 'row',
    tableClass = TABLE_TEXT[density],
    headerRowClass = DEFAULT_HEADER_ROW,
    headerCellClass = HEADER_CELL[density],
    cellClass = CELL[density],
    footerCellClass = FOOTER_CELL[density],
    rowProps,
    renderSubRow,
  }: DataTableProps<T>,
  ref: React.Ref<DataTableHandle>
) {
  const tones = TONES[on];
  const holdHeader = stickyHeader || !!maxHeight;
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  // Readback, post-commit, mount included (see the prop's comment). Effect
  // rather than inside the setState updater: an updater can run twice in
  // StrictMode, and a consumer's persistence write is a side effect.
  useEffect(() => {
    onSortingChange?.(sorting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // USER toggles only, no first-seen bookkeeping: a key absent here follows the
  // consumer's CURRENT initialCollapsed, so a refetch that changes the default
  // re-derives instead of remembering an answer about a set that has moved on.
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // Sortable columns fall onto the house comparator unless they name one.
  const resolvedColumns = useMemo(
    () =>
      columns.map((col) =>
        'accessorFn' in col || 'accessorKey' in col
          ? { sortingFn: 'house' as const, ...col }
          : col
      ),
    [columns]
  );

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    state: {
      sorting,
      columnVisibility: columnVisibility ?? NO_VISIBILITY,
    },
    onSortingChange: setSorting,
    getRowId,
    // Straight through, unread by this component. The cast is the whole reason
    // the prop is `object`: tanstack's option is typed against a global
    // augmentable interface, and this keeps that global out of the public
    // props. setOptions runs during render, so a cell reads the CURRENT meta
    // on the same pass the parent handed it over.
    meta: tableMeta as TableMeta<T> | undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortingFns: { house: houseCompare },
    enableSorting: sortable,
    // Locked table-wide: tanstack's defaults would let a third click CLEAR
    // the sort, would flip a numeric column's first click to descending
    // based on the first row's value type, and would stack a second sort
    // column on shift-click (with removal off, un-peelable) — all three
    // diverge from every page here.
    enableSortingRemoval: false,
    sortDescFirst: false,
    enableMultiSort: false,
    // No pagination here, but the sorted-row-model memo still calls
    // _autoResetPageIndex on every data change, and that internal setState
    // sprays act() warnings through every consumer's tests.
    autoResetPageIndex: false,
  });

  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const visibleColCount = visibleColumns.length;
  // A tfoot exists only when a VISIBLE column asks for one. Hiding the column
  // that carries a total takes its footer cell with it, the same way it takes
  // its th and td — otherwise the band would keep a cell the grid no longer
  // has and every figure to its right would slide one column over.
  const hasColumnFooters = visibleColumns.some((c) => c.columnDef.footer != null);

  // ONE visual order. Grouping reorders rows and a collapsed group withdraws
  // its own from the field, so the WHOLE plan — group order, each group's
  // header node, each group's collapse state — is derived once here and feeds
  // BOTH the renderer and the keyboard dialect. Two derivations would paint the
  // ring on one row and open another.
  //
  // Effective collapsed for a key: the user's toggle if there is one, else the
  // consumer's initialCollapsed, else open. Never collapsed for a group that
  // cannot collapse, so a stale toggle left by a changed `collapsible` cannot
  // hide rows behind an affordance that no longer exists.
  //
  // renderHeader is called with the state derived from `collapsible`, BEFORE
  // the null check below can force a headerless group open. Harmless: a
  // consumer returns null for the KEY, not for one collapse state, so the
  // discarded call cannot leave a caret facing the wrong way.
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const byKey = new Map<string, Row<T>[]>();
    for (const row of rows) {
      const key = groupBy.key(row.original);
      const bucket = byKey.get(key);
      if (bucket) bucket.push(row);
      else byKey.set(key, [row]);
    }
    const keys = groupBy.order
      ? groupBy.order(Array.from(byKey.keys()))
      : Array.from(byKey.keys()).sort((a, b) => a.localeCompare(b));
    return keys.map((key): GroupPlan<T> => {
      const groupRows = byKey.get(key) ?? [];
      const canCollapse = groupBy.collapsible?.(key) ?? false;
      const collapsed = canCollapse
        ? collapsedGroups[key] ?? groupBy.initialCollapsed?.(key) ?? false
        : false;
      const header = groupBy.renderHeader(
        key,
        groupRows.map((r) => r.original),
        { collapsed }
      );
      // A collapsible group with no header row is a contradiction: nothing to
      // expand it from. It renders headerless and OPEN, and the dev check
      // below names the key.
      const headerless = header == null;
      return {
        key,
        rows: groupRows,
        header,
        headerless,
        collapsible: canCollapse && !headerless,
        collapsed: collapsed && !headerless,
      };
    });
  }, [rows, groupBy, collapsedGroups]);
  const displayRows = useMemo(
    () => (grouped ? grouped.flatMap((g) => (g.collapsed ? [] : g.rows)) : rows),
    [grouped, rows]
  );

  // One flat sequence of body lines, so the first and last can take the stack's corners. Group
  // bands break the sheet dialect's nth-child math; the two are mutually exclusive by design. The
  // ROW dialect survives grouping: the keyboard indexes displayRows, the same flattened order
  // drawn here, which is why the running index advances by DISPLAYED rows only. A collapsed group
  // contributes its band and nothing else.
  const lines = useMemo((): Line<T>[] => {
    if (!grouped) return displayRows.map((row, index) => ({ kind: 'row', key: row.id, row, index }));
    const out: Line<T>[] = [];
    let runningIndex = 0;
    for (const group of grouped) {
      if (!group.headerless) out.push({ kind: 'band', key: `${BAND_KEY}${group.key}`, group });
      if (group.collapsed) continue;
      for (const row of group.rows) out.push({ kind: 'row', key: row.id, row, index: runningIndex++ });
    }
    return out;
  }, [grouped, displayRows]);

  // A refetch over rows already on screen keeps them, dimmed and busy, instead of swapping in
  // loadingContent. Only a table with no rows yet shows that.
  const refreshing = loading && rows.length > 0;
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const { shown, entering } = useRowMotion(lines, bodyRef);

  if (process.env.NODE_ENV === 'development' && initialSorting) {
    for (const s of initialSorting) {
      const col = table.getColumn(s.id);
      if (!col || !col.getCanSort()) {
        // eslint-disable-next-line no-console
        console.error(
          `DataTable: initialSorting names "${s.id}" but that column cannot sort ` +
            '(missing, sorting disabled, or it has no accessorFn).'
        );
      }
    }
  }

  if (process.env.NODE_ENV === 'development' && grouped && groupBy) {
    for (const group of grouped) {
      if (group.headerless && groupBy.collapsible?.(group.key)) {
        // eslint-disable-next-line no-console
        console.error(
          `DataTable: group "${group.key}" is collapsible but its renderHeader ` +
            'returned nothing, so there would be no header row to expand it from. ' +
            'It renders headerless and stays open.'
        );
      }
    }
  }

  useImperativeHandle(
    ref,
    () => {
      const exportRows = () => {
        const cols = table
          .getVisibleLeafColumns()
          .filter((c) => c.columnDef.meta?.csv || c.accessorFn);
        const headers = cols.map((c) => {
          const def = c.columnDef;
          return def.meta?.csvHeader ?? (typeof def.header === 'string' ? def.header : c.id);
        });
        const out = table.getRowModel().rows.map((r) =>
          cols.map((c) => {
            const csv = c.columnDef.meta?.csv;
            return csv ? csv(r.original) : (r.getValue(c.id) as CsvValue);
          })
        );
        return { headers, rows: out };
      };
      return {
        resetSelection: () => setSelectedIndex(0),
        exportRows,
        exportCsv: (basename: string) => {
          if (loading) return null;
          const { headers, rows: out } = exportRows();
          downloadCsv(csvFilename(basename), toCsv(headers, out));
          return out.length;
        },
      };
    },
    [table, loading]
  );

  // stickyHeader means "take the rest of the viewport": the shell caps itself at the space from
  // its own top edge to the window's bottom, less what the page keeps below it (ui/viewport).
  // No page sizes itself around the navbar or its own filter rows: the vessel does. min 320px:
  // a short viewport degrades to page scroll instead of a crushed table.
  const shellRef = useRef<HTMLDivElement | null>(null);
  useViewportFill(shellRef, { mode: 'max', min: 320, enabled: !!stickyHeader });

  const onOpen = rowKeyboard?.onOpen;
  const onDiscard = rowKeyboard?.onDiscard;
  const hasKeyboard = !!rowKeyboard;
  const keyboardEnabled = hasKeyboard && !loading;

  // The landing set, LATCHED per batch. A row animates in only when it is
  // new, the batch overlaps the previous one (same pile, updated), and the
  // landing party is small — a widened filter or a switched view re-showing
  // many rows is a set change, not a landing. Latched on the row array's
  // identity so re-renders inside the 0.18s window never strip the class
  // mid-flight. Ref mutated during render on purpose: it is idempotent for
  // one batch, and an effect would run only after the class was already gone.
  const landingRef = useRef<{ batch: unknown; ids: Set<string>; seen: Set<string> }>({
    batch: null,
    ids: new Set(),
    seen: new Set(),
  });
  {
    const latch = landingRef.current;
    if (latch.batch !== displayRows) {
      const fresh = displayRows.filter((r) => !latch.seen.has(r.id));
      const overlaps = fresh.length < displayRows.length;
      latch.ids =
        overlaps && fresh.length > 0 && fresh.length <= LANDING_LIMIT
          ? new Set(fresh.map((r) => r.id))
          : new Set();
      latch.seen = new Set(displayRows.map((r) => r.id));
      latch.batch = displayRows;
    }
  }
  const landingIds = landingRef.current.ids;

  // The landing party also gives off light for a moment, then settles. The
  // timer is cleared only by the next landing or unmount: every batch mints a
  // fresh (often empty) set, and clearing on that would leave the glow stuck.
  const [glowing, setGlowing] = useState<ReadonlySet<string>>(NO_IDS);
  const glowTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (landingIds.size === 0) return;
    setGlowing(landingIds);
    window.clearTimeout(glowTimer.current);
    glowTimer.current = window.setTimeout(() => setGlowing(NO_IDS), LANDING_GLOW_MS);
  }, [landingIds]);
  useEffect(() => () => window.clearTimeout(glowTimer.current), []);

  // Clamp when the pile shrinks (a discard must not strand the selection).
  // Selection is a keyboard-dialect concept: without the dialect there is no
  // state to keep, and the setState here would only feed act() warnings.
  useEffect(() => {
    if (!hasKeyboard) return;
    setSelectedIndex((i) => Math.min(i, Math.max(0, displayRows.length - 1)));
  }, [displayRows.length, hasKeyboard]);

  useEffect(() => {
    if (!keyboardEnabled) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
      // A menu or a modal owns the keyboard while it is open: Enter on a
      // menuitem must not also open the selected row, and D from inside the
      // help drawer must not navigate the page out from behind it. Not every
      // button, though: focus rests on a tab button after a click, and J/K
      // from there is the rapid-entry path.
      if (target?.closest?.('[role="menu"], [role="menuitem"], [role="dialog"], [aria-modal="true"]')) return;
      if (displayRows.length === 0) return;
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(displayRows.length - 1, i + 1));
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // The row lookup lives INSIDE the updater: the listener's closure
        // never re-binds on selection change, so this is the only place the
        // current index is readable.
        setSelectedIndex((i) => {
          const row = displayRows[i];
          if (row && onOpen) onOpen(row.original);
          return i;
        });
      } else if ((e.key === 'd' || e.key === 'D') && onDiscard) {
        e.preventDefault();
        setSelectedIndex((i) => {
          const row = displayRows[i];
          if (row) onDiscard(row.original);
          return i;
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [displayRows, keyboardEnabled, onOpen, onDiscard]);

  const firstColId = visibleColumns[0]?.id;

  // Rows whose detail is still on screen, closing included: such a row's bottom corners stay
  // square until the detail has gone, so the two never both round for a frame.
  const [subPresent, setSubPresent] = useState<ReadonlySet<string>>(() => new Set());
  const markSub = (id: string, present: boolean) =>
    setSubPresent((prev) => {
      if (prev.has(id) === present) return prev;
      const next = new Set(prev);
      if (present) next.add(id);
      else next.delete(id);
      return next;
    });

  // The outer corners a body line owns: the stack's first line takes the top
  // two, its last line (or that line's open sub-row) the bottom two.
  const cornersFor = (isFirst: boolean, isLast: boolean, side: 'left' | 'right' | 'both'): Corner[] => {
    const out: Corner[] = [];
    if (isFirst && side !== 'right') out.push('tl');
    if (isFirst && side !== 'left') out.push('tr');
    if (isLast && side !== 'right') out.push('bl');
    if (isLast && side !== 'left') out.push('br');
    return out;
  };

  const renderRow = (
    row: Row<T>,
    index: number,
    ordinal: number,
    isFirst: boolean,
    isLast: boolean,
    leaving: boolean
  ) => {
    const selected = !leaving && !!rowKeyboard && index === selectedIndex;
    const landing = !leaving && landingIds.has(row.id);
    // By drawn place, a leaving row included, so the rows below keep their tones until it has gone.
    const stripe = striped && ordinal % 2 === 1;
    const extra = rowProps ? rowProps(row.original, { index, selected }) : {};
    const { onClick: consumerClick, className: consumerClass, style: consumerStyle, ...rest } = extra;
    const onClick = leaving
      ? undefined
      : rowKeyboard
      ? (e: React.MouseEvent<HTMLTableRowElement>) => {
          setSelectedIndex(index);
          if (consumerClick) (consumerClick as React.MouseEventHandler<HTMLTableRowElement>)(e);
        }
      : (consumerClick as React.MouseEventHandler<HTMLTableRowElement> | undefined);
    // A row that is itself a tab stop is where focus lands when a focused neighbour leaves.
    const ownLanding = typeof rest.tabIndex === 'number' && rest.tabIndex >= 0;
    const rowClass = [
      ROW_BASE,
      stripe ? tones.stripe : tones.row,
      selected ? SELECTED_ROW : '',
      landing ? ROW_LAND : '',
      !leaving && !landing && entering.has(row.id) ? ROW_ENTER : '',
      // The selection edge and the glow are both box-shadows; selection wins.
      glowing.has(row.id) && !selected && !leaving ? ROW_GLOW : '',
      leaving ? ROW_LEAVE : '',
      (consumerClass as string) || '',
    ]
      .filter(Boolean)
      .join(' ');
    const sub = renderSubRow && !leaving ? renderSubRow(row.original) : null;
    const rowIsLast = isLast && (sub == null || sub === false) && !subPresent.has(row.id);
    const cells = row.getVisibleCells();
    // A page's own row tint lives on the tr, so the pinned cell inherits it rather than repaint
    // the plain tone over it. Untinted rows keep the opaque solid so scrolled content never shows.
    const pinTone = selected
      ? 'bg-surface-container-highest'
      : consumerClass
      ? 'bg-inherit'
      : stripe ? tones.solidStripe : tones.solidRow;
    const style = consumerStyle as React.CSSProperties | undefined;
    return (
      <React.Fragment key={row.id}>
        <tr
          onClick={onClick}
          className={rowClass}
          data-selected={selected ? 'true' : undefined}
          data-line-key={row.id}
          data-handoff={ownLanding && !leaving ? '' : undefined}
          {...rest}
          {...(leaving ? (ownLanding ? LEAVING_STOP : LEAVING) : {})}
          // Inline, so a page's own dim class (opacity-50 on a disabled row) cannot hold the fade up.
          style={leaving ? { ...style, opacity: 0 } : style}
        >
          {cells.map((cell, ci) => {
            const meta = cell.column.columnDef.meta;
            const attrs = meta?.cellAttrs ? meta.cellAttrs(row.original) : undefined;
            const pinned = pinFirstColumn && cell.column.id === firstColId;
            const side =
              cells.length === 1 ? 'both' : ci === 0 ? 'left' : ci === cells.length - 1 ? 'right' : null;
            const corners = side ? cornersFor(isFirst, rowIsLast, side) : [];
            return (
              <td
                key={cell.id}
                {...attrs}
                className={[
                  cellClass,
                  alignClass(meta?.align),
                  meta?.numeric ? 'tabular-nums' : '',
                  // The tr's accent edge is an inset shadow the opaque pinned cell would cover.
                  pinned ? `${PINNED_CELL} ${pinTone} ${selected ? 'shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]' : ''}` : '',
                  meta?.cellClass ?? '',
                  attrs?.className ?? '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={mergeStyle(attrs?.style, corners.length ? cornerStyle(corners, pinned) : undefined)}
                data-corners={corners.length ? corners.join(' ') : undefined}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
        {renderSubRow ? (
          <SubRow
            colSpan={visibleColCount}
            content={sub}
            tone={tones.sub}
            style={cornerStyle(cornersFor(false, isLast, 'both'))}
            onPresent={(present) => markSub(row.id, present)}
            lineKey={`${SUB_KEY}${row.id}`}
          />
        ) : null}
      </React.Fragment>
    );
  };

  // Flips the EFFECTIVE state, not the stored one: a group collapsed by
  // initialCollapsed has no entry to negate, and negating the absence would
  // write back the state it is already in.
  const toggleGroup = (key: string, collapsed: boolean) =>
    setCollapsedGroups((prev) => ({ ...prev, [key]: !collapsed }));

  const renderBand = (group: GroupPlan<T>, isFirst: boolean, isLast: boolean, leaving: boolean) => {
    const corners = cornersFor(isFirst, isLast, 'both');
    return (
      <tr
        key={`${BAND_KEY}${group.key}`}
        className={[groupBy?.rowClass ?? BAND_ROW, BAND_MOTION, leaving ? ROW_LEAVE : ''].filter(Boolean).join(' ')}
        data-line-key={`${BAND_KEY}${group.key}`}
        {...(leaving ? LEAVING : {})}
        style={leaving ? { opacity: 0 } : undefined}
      >
        {/* The whole cell is the pointer target and an inner button carries the
            keyboard path, exactly as a sortable header does. The button stops
            propagation so one click never toggles twice. A non-collapsible group
            has no button, no caret and no pointer. */}
        <td
          colSpan={visibleColCount}
          className={[groupBy?.cellClass ?? BAND_CELL[density], group.collapsible ? 'cursor-pointer' : '']
            .filter(Boolean)
            .join(' ')}
          style={cornerStyle(corners)}
          data-corners={corners.length ? corners.join(' ') : undefined}
          onClick={group.collapsible ? () => toggleGroup(group.key, group.collapsed) : undefined}
        >
          {group.collapsible ? (
            <button
              type="button"
              aria-expanded={!group.collapsed}
              onClick={(e) => {
                e.stopPropagation();
                toggleGroup(group.key, group.collapsed);
              }}
              className={`inline-flex items-center gap-2 select-none rounded-control ${FOCUS}`}
            >
              <IconChevron open={!group.collapsed} className="h-3.5 w-3.5 flex-none text-on-surface-variant" />
              {group.header}
            </button>
          ) : (
            group.header
          )}
        </td>
      </tr>
    );
  };

  // A shown header band opens the stack, so it holds the top corners and the body only the bottom.
  const bodyOpens = hideHeader;

  const renderBody = () => {
    // Bare structure only: padding and color belong to the consumer's node,
    // because every page styles (and test-pins) its own copy. The row itself
    // is one slat, its outer corners cut.
    const loneCorners: Corner[] = bodyOpens ? ['tl', 'tr', 'bl', 'br'] : ['bl', 'br'];
    const lone = (content: React.ReactNode) => (
      <tr className={tones.row}>
        <td
          colSpan={visibleColCount}
          className="text-center"
          style={cornerStyle(loneCorners)}
          data-corners={loneCorners.join(' ')}
        >
          {content}
        </td>
      </tr>
    );
    if (rows.length === 0) return lone(loading ? loadingContent : empty);

    const last = shown.length - 1;
    let ordinal = 0;
    return shown.map(({ item, leaving }, i) => {
      const isFirst = bodyOpens && i === 0;
      if (item.kind === 'band') return renderBand(item.group, isFirst, i === last, leaving);
      return renderRow(item.row, leaving ? -1 : item.index, ordinal++, isFirst, i === last, leaving);
    });
  };

  // 'replace' keeps the vessel and swaps only the field's contents, so a page
  // that arrives loading and then fills does not flip between a bare box and a
  // table — and the component's own state (selection, the landing latch) is
  // never unmounted by an empty fetch.
  const replaced = emptyMode === 'replace' && rows.length === 0;

  // The busy line sits at the top of the body, under the header band, whatever height a page's
  // header content gives it.
  const busyTrackRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const track = busyTrackRef.current;
    const body = bodyRef.current;
    const tableEl = body?.parentElement;
    if (!refreshing || !track || !body || !tableEl) return;
    track.style.top = `${body.getBoundingClientRect().top - tableEl.getBoundingClientRect().top}px`;
  }, [refreshing]);

  const tableMarkup = (
    <table
      className={`${tableClass} border-separate ${striped ? '[border-spacing:0]' : '[border-spacing:0_1px]'}`}
      aria-busy={refreshing || undefined}
    >
      <thead className={hideHeader ? 'sr-only' : undefined}>
        {table.getHeaderGroups().map((headerGroup, gi) => (
          <tr key={headerGroup.id} className={headerRowClass || undefined}>
            {headerGroup.headers.map((header, hi) => {
              const column = header.column;
              const count = headerGroup.headers.length;
              const side = count === 1 ? 'both' : hi === 0 ? 'left' : hi === count - 1 ? 'right' : null;
              const corners = !bodyOpens && gi === 0 && side ? cornersFor(true, false, side) : [];
              const meta = column.columnDef.meta;
              const canSort = column.getCanSort();
              const sorted = column.getIsSorted();
              const pinned = pinFirstColumn && column.id === firstColId;
              const hold = holdHeader && pinned
                ? PINNED_STICKY_HEADER_CELL
                : holdHeader
                ? STICKY_HEADER_CELL
                : pinned
                ? PINNED_HEADER_CELL
                : '';
              const className = [
                headerCellClass,
                hold,
                alignClass(meta?.align),
                canSort ? 'group/sort cursor-pointer hover:text-on-surface transition-colors duration-quick ease-emphasized' : '',
                meta?.headerClass ?? '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <th
                  key={header.id}
                  scope="col"
                  className={className}
                  style={cornerStyle(corners)}
                  data-corners={corners.length ? corners.join(' ') : undefined}
                  // The whole cell is the pointer target (as every page has it
                  // today); the inner button carries the keyboard path. The
                  // button stops propagation so one click never toggles twice.
                  onClick={canSort ? column.getToggleSortingHandler() : undefined}
                  // Only sortable headers advertise sort state; a plain table
                  // must not wear aria-sort="none" everywhere.
                  aria-sort={
                    canSort
                      ? sorted === 'asc'
                        ? 'ascending'
                        : sorted === 'desc'
                        ? 'descending'
                        : 'none'
                      : undefined
                  }
                >
                  {canSort ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        column.getToggleSortingHandler()?.(e);
                      }}
                      className={`inline-flex items-center gap-1 select-none rounded-control ${FOCUS}`}
                    >
                      {flexRender(column.columnDef.header, header.getContext())}
                      {/* Always in place, so a label never shifts when the sort
                          moves. Unsorted it shows on hover, pointing the way
                          the first click will sort. */}
                      <span
                        aria-hidden="true"
                        data-sort-mark=""
                        className={`inline-flex transition-[opacity,transform,color] duration-spring ease-spring motion-reduce:transition-[opacity,color] ${
                          sorted
                            ? 'text-primary opacity-100'
                            : 'text-on-surface-variant opacity-0 group-hover/sort:opacity-100 group-focus-within/sort:opacity-100 [@media(hover:none)]:opacity-40'
                        } ${sorted === 'desc' ? '' : 'rotate-180'}`}
                      >
                        <IconChevronDown className="h-3 w-3" />
                      </span>
                    </button>
                  ) : (
                    flexRender(column.columnDef.header, header.getContext())
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody ref={bodyRef} className={refreshing ? BODY_BUSY : BODY_REST}>
        {renderBody()}
      </tbody>
      {/* The column-footer band. INSIDE the table and inside the scrollport on
          purpose: that is the only way a total sits under the column it totals
          and travels with it when the field scrolls sideways. The prose
          `footer` prop is the other half of the pair — it lives below the
          field, spans the vessel, and knows nothing about columns.

          td, not th: these are values, and a th here would offer every screen
          reader a second set of column names.

          It renders only over real rows. A totals band under a "nothing here"
          row would state zeros as a fact, and under a spinner it would state
          the PREVIOUS answer; a consumer cannot opt out per render either,
          since contract 1 forbids rebuilding `columns` to drop a footer. In
          'replace' mode the whole table markup is gone already, so the band
          goes with it. A refresh over rows already shown keeps it, dimmed with
          the rows it sums: both are the previous answer, and the table says it
          is busy. Dropping it there would jump the table's height twice.

          One cell per visible leaf column, flat column defs only: no colSpan,
          no isPlaceholder, which is the same assumption the thead above makes.
          A columnDef with a nested `columns:` array would misalign both bands,
          and nothing in this app uses one.

          NOT sticky. A page on `stickyHeader` scrolls the band off the bottom
          with the last row, so a totals figure is a scroll away rather than
          always in view. Deliberate non-feature for now: sticky-bottom here
          means owning the same edge the prose band takes when a page passes
          both, and that fight is worth having only once a page needs it. */}
      {hasColumnFooters && displayRows.length > 0 ? (
        <tfoot className={refreshing ? BODY_BUSY : BODY_REST}>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                return (
                  <td
                    key={header.id}
                    className={[
                      footerCellClass,
                      alignClass(meta?.align),
                      meta?.numeric ? 'tabular-nums' : '',
                      meta?.footerClass ?? '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {/* A column with no footer still gets its cell: the band
                        is a grid row, and flexRender of nothing is nothing.
                        getContext(), not a footer-specific one: v8 hands a
                        footer def the same HeaderContext a header gets, so a
                        total comes off `props.table.getRowModel()` (or the
                        page's own closure) — there is no row in scope. */}
                    {flexRender(header.column.columnDef.footer, header.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tfoot>
      ) : null}
    </table>
  );

  return (
    <div
      ref={shellRef}
      className={`${tones.backdrop} ${tones.head} min-w-0 overflow-hidden rounded-panel${stickyHeader ? ' flex flex-col' : ''}`}
    >
      {strip ? <div className={STRIP[density]}>{strip}</div> : null}
      {/* The field. Inside the shell so the radius clips it, and it is the
          scrollport the sticky header holds against. `dt-scroll` (index.css)
          dresses its scrollbar; SCROLL_THUMB rings the thumb in the backdrop.
          Under stickyHeader the shell is a capped flex column and the field
          takes what the strip and footer leave (min-h-0 so it can actually
          shrink and scroll); maxHeight caps the field itself. `relative` makes
          it the containing block for a cell's absolute children (an sr-only
          header label): without it they escape the scroll clip and widen the
          whole page on a phone. */}
      <div
        className={`relative overflow-x-auto dt-scroll ${SCROLL_THUMB}${stickyHeader ? ' min-h-0 grow' : ''}`}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {replaced ? (
          <div className={`rounded-panel ${tones.solidRow}`}>{loading ? loadingContent : empty}</div>
        ) : (
          <>
            <div aria-hidden="true" className={BUSY_LINE}>
              <div ref={busyTrackRef} className={BUSY_TRACK[refreshing ? 'on' : 'off']}>
                {refreshing ? <div className={BUSY_SWEEP} /> : null}
              </div>
            </div>
            {tableMarkup}
          </>
        )}
      </div>
      {footer ? <div className={FOOTER[density]}>{footer}</div> : null}
    </div>
  );
}

// forwardRef erases generics; the cast restores them for consumers.
export const DataTable = forwardRef(DataTableInner) as <T>(
  props: DataTableProps<T> & { ref?: React.Ref<DataTableHandle> }
) => React.ReactElement;
